import { Button, Col, Form, Input, Row, Select, Spin, Typography, useForm } from "@pankod/refine-antd";
import { useCan, useOne } from "@pankod/refine-core";
import { IExpertConnect, IExpertConnectMetadata, IOutReachFormFormat, IOutreachTexts } from "interfaces";
import { useEffect, useState } from "react";
import { DATAPROVIDER_LOOKUP, DATAPROVIDER_OUTREACH, RESOURCE_PATH } from "scripts/site";

export declare type outreachFormProps = {
    categoryType?: string,
    onFormSubmit: () => void
};

export const OutreachForm: React.FC<outreachFormProps> = ({
    categoryType,
    onFormSubmit
}) => {
    const { isLoading, data: metadata } = useOne<IExpertConnectMetadata>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.METADATA,
        id: RESOURCE_PATH.EXPERT_OUTREACH,
        queryOptions: {
            enabled: true,
            staleTime: 300000,
        }
    });
    const defaultFormLabels = {
        errorMessage: "Unable to process your request. Please retry. If problem persists, please reach out to helium support team.",
        successMessage: "Your query is successfully directed to expert.",
        disciplineText: "Discipline",
        placeholderDisciplineText: "Select area of connect",
        expertiseText: "Field of Expertise",
        placeholderExpertText: "Field of Expertise",
        subjectText: "Topic",
        placeholderSubjectText: "Intent of connect in short",
        questionText: "Question",
        placeholderQuestionText: "Describe the feedback/question/issue and help required",
        additionalText: "Addition details",
        placeholderAdditionalText: "Describe the issue, cause and effects",
        buttonText: "Send Query"
    } as IOutreachTexts;

    const defaultUIControls = {
        questionTextAreaRows: 3,
        captureAdditionalDetails: true,
        additionalDetailsTextAreaRows: 4
    } as IOutReachFormFormat;

    const expertAreas = metadata?.data.config.expertAreas;
    const [expertDiscipline, setExpertDiscipline] = useState("");
    const [formTextLabels, setFormLabels] = useState(defaultFormLabels);
    const [formUIControls, setFormUIControls] = useState(defaultUIControls);

    const { form, formProps, saveButtonProps } = useForm<IExpertConnect>({
        dataProviderName: DATAPROVIDER_OUTREACH,
        resource: RESOURCE_PATH.EXPERT_OUTREACH,
        redirect: false,
        successNotification: { type: "success", message: formTextLabels.successMessage },
        errorNotification: { type: "error", message: formTextLabels.errorMessage }
    });

    useEffect(() => {
        // Passed with Category, load only specific category details
        if (categoryType) {
            let area = expertAreas?.find(x => x.id === categoryType);
            setExpertDiscipline(area?.category || "");
            if (area?.formTexts) {
                setFormLabels({ ...defaultFormLabels, ...area.formTexts });
            }
            if (area?.uiControls) {
                setFormUIControls({ ...defaultUIControls, ...area.uiControls });
            }
            form.setFieldsValue({
                category: area?.category
            })
        }
    // eslint-disable-next-line 
    }, [categoryType, expertAreas, form, isLoading]);

    const canListExpertOutreach = useCan({
        resource: RESOURCE_PATH.EXPERT_OUTREACH,
        action: "create"
    });

    return (canListExpertOutreach.data?.can ?
        <Spin spinning={isLoading}>
            <Form layout="vertical" form={form} {...formProps} autoComplete="off">
                <Row gutter={[10, 0]} align="bottom">
                    <Col xs={24} xl={24} md={8}>
                        <Form.Item
                            label={formTextLabels.disciplineText}
                            name="category"
                            hidden={categoryType ? true : false}
                            rules={[
                                {
                                    required: true,
                                    validator(_rule, value) {
                                        let initialAreaCategory = expertDiscipline;
                                        setExpertDiscipline(value)
                                        if (initialAreaCategory !== value) {
                                            form.resetFields(['primaryExpertArea']);
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <Select
                                allowClear
                                placeholder={formTextLabels.placeholderDisciplineText}
                                disabled={categoryType ? true : false}
                            >
                                {expertAreas?.sort().map((m, i) => (
                                    <Select.Option value={m.category} key={i}>{m.category}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={24} md={8}>
                        <Form.Item
                            label={formTextLabels.expertiseText}
                            name="primaryExpertArea"
                            dependencies={['category']}
                            rules={[
                                {
                                    required: true,
                                }
                            ]}
                        >
                            <Select
                                allowClear
                                placeholder={formTextLabels.placeholderExpertText}
                            >
                                {expertAreas?.find(x => x.category === expertDiscipline)?.areas.sort().map((m, i) => (
                                    <Select.Option value={m.name} key={i}>{m.name}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={24} md={8}>
                        <Form.Item
                            label={formTextLabels.subjectText}
                            name="subject"
                            rules={[
                                {
                                    required: true,
                                    whitespace: true,
                                    min: 10,
                                    max: 50
                                },
                            ]}
                        >
                            <Input placeholder={formTextLabels.placeholderSubjectText} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={24} md={8}>
                        <Form.Item
                            label={formTextLabels.questionText}
                            name="question"
                            rules={[
                                {
                                    required: true,
                                    whitespace: true,
                                    min: 20,
                                    max: 500
                                },
                            ]}
                        >
                            <Input.TextArea rows={formUIControls.questionTextAreaRows} placeholder={formTextLabels.placeholderQuestionText} />
                        </Form.Item>
                    </Col>
                    {formUIControls.captureAdditionalDetails &&
                        <Col xs={24} xl={24} md={8}>
                            <Form.Item
                                label={formTextLabels.additionalText}
                                name="additionalInformation"
                                rules={[
                                    {
                                        required: false,
                                        whitespace: true,
                                        min: 50,
                                        max: 1000
                                    },
                                ]}
                            >
                                <Input.TextArea rows={formUIControls.additionalDetailsTextAreaRows} placeholder={formTextLabels.placeholderAdditionalText} />
                            </Form.Item>
                        </Col>
                    }
                    <Col xs={24} xl={24} md={8}>
                        <Form.Item>
                            <Button
                                {...saveButtonProps}
                                style={{ width: "100%" }}
                                htmlType="submit"
                                type="primary"
                                onClick={() => {
                                    onFormSubmit()
                                }}
                            >
                                {formTextLabels.buttonText}
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Spin>
        : <Typography.Title level={4} style={{ color: "#e0529c" }} >You are not authorized to perform this operation</Typography.Title >
    );
}